import { toast } from "react-toastify";

import { CopyIconButton, useCopyAcknowledgement } from "components/shared/CopyIconButton";

import { useUser, useUserEvents } from "../../hooks";
import useFeatureFlags from "../../hooks/useFeatureFlags";
import { IColumns } from "../../models/columns";
import { EntityKind } from "../../models/entityKind";

type CopyButtonT = {
  rows: Record<string, string>[];
  columns: IColumns[];
  selectedProperties: string[];
  entityKind?: EntityKind;
  // Tags the copy event, as the monthly export does, so monthly copies can be counted.
  view?: string;
};

export default function CopyButton(props: CopyButtonT) {
  const { copied, acknowledge } = useCopyAcknowledgement();
  const { hasTrialAccount, isReadonly } = useUser();
  const { sendCopyDataTableEvent } = useUserEvents();
  const { hasFeature } = useFeatureFlags();
  const hasMidstreamColumns =
    Array.isArray(props.selectedProperties) &&
    props.selectedProperties.some((p) => p.startsWith("Midstream."));
  const canExportMidstreamColumns = hasFeature("Midstream Export");
  const isFacility = props.entityKind === EntityKind.Facility;
  const canShowCopyButton = !(isFacility && !canExportMidstreamColumns);
  function formatTableDataForClipboard(
    columns: IColumns[],
    rows: Record<string, string>[]
  ) {
    const header = columns.map((col) => col.name).join("\t");
    const data = rows
      .map((row) => columns.map((col) => row[col.key]).join("\t"))
      .join("\n");
    return `${header}\n${data}`;
  }

  function copyTable(columns: IColumns[], rows: Record<string, string>[]) {
    if (hasMidstreamColumns && !canExportMidstreamColumns) {
      toast.error("Your account does not have access to export midstream columns.");
      return;
    }

    // Send user event for data table copy
    sendCopyDataTableEvent({
      source: "data-table",
      rowCount: rows.length,
      columnCount: columns.length,
      ...(props.view && { view: props.view })
    });

    const formattedData = formatTableDataForClipboard(columns, rows);
    navigator.clipboard
      .writeText(formattedData)
      .then(() => {
        acknowledge();
      })
      .catch((err) => {
        // eslint-disable-next-line no-console -- Necessary for debugging copy errors.
        console.error(`Failed to copy. ${err}`);
      });
  }

  return (
    <>
      {!hasTrialAccount && !isReadonly && canShowCopyButton && (
        <CopyIconButton
          tooltip="Copy Data Table"
          successMessage="Data Table Copied to Clipboard!"
          copied={copied}
          onClick={copyTable.bind(null, props.columns, props.rows)}
          data-testid="copy-data-table-button"
        />
      )}
    </>
  );
}
